import graphql__b3c9099aae43cfdf6b5d from "./__generated__/ContributionsDatavizSearchFiltersThemes_themes.graphql.js";import { useCallback, useState, useMemo, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch } from "react-redux";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useResourcesList } from "../../../ResourcesList/resourcesList";
import { toggleSelectedTheme } from "../../../../reducers/resourcesList/search";
import { useFormatMessage } from "../../../Intl/Context";
import Button from "../../../Buttons/Button";
import Icon from "../../../Utils/Icon";
import { uniqueId } from "lodash";

const THEMES_EXCERPT_COUNT = 4;

const ContributionsDatavizSearchFiltersThemes = props => {
  const dispatch = useDispatch();

  const themes = useFragment(
    ContributionsDatavizSearchFiltersThemes_themes,
    props.themes
  );
  const [showMore, setShowMore] = useState(themes.length <= THEMES_EXCERPT_COUNT);

  const t = useFormatMessage();
  const showMoreLabel = t(intlMessages.showMore);
  const showLessLabel = t(intlMessages.showLess);

  const { resourcesListKey, search } = useResourcesList();
  const { selectedThemes } = search;
  const selectedThemeIds = selectedThemes.map(({ id }) => id);

  const collapsible = themes.length > THEMES_EXCERPT_COUNT;

  const toggleFilter = useCallback(
    theme => dispatch(toggleSelectedTheme(resourcesListKey, theme)),
    [dispatch, resourcesListKey]
  );

  const className = classNames([
    "themeList",
    props.className,
    { partial: collapsible && !showMore }
  ]);

  const displayableThemes = showMore ? themes : themes.slice(0, THEMES_EXCERPT_COUNT);
  const themeIds = themes.map(({ id }) => id);

  // Check whether themes from another theme level are currently selected
  const otherThemeLevelSelected = selectedThemeIds.some(id => !themeIds.includes(id));

  // Build a cached array of all the ids of the parents of the themes of this level
  const parentThemeIds = useMemo(
    () =>
      themes.reduce((ids, { parent }) => {
        const parentIds = [];
        if (parent) parentIds.push(parent.id);
        if (parent?.parent) parentIds.push(parent.parent.id);

        return ids.concat(parentIds);
      }, []),
    [themes]
  );

  // Chceck whether any of the currently selected themes are parent of the themes
  const hasSelectedParentThemes = selectedThemeIds.some(id =>
    parentThemeIds.includes(id)
  );

  const filterContainerDOMId = useRef(uniqueId("dataviz-search-theme-filter-")).current;

  return (
    <div className="themes-filters-list">
      <fieldset className={className} id={filterContainerDOMId}>
        {displayableThemes.map(theme => {
          // Check whether the theme is currently selected
          const themeSelected = selectedThemeIds.includes(theme.id);
          // Check whether one of its parent themes are currently selected
          const parentThemeSelected =
            (theme.parent && selectedThemeIds.includes(theme.parent.id)) ||
            (theme.parent?.parent && selectedThemeIds.includes(theme.parent.parent.id));

          // A theme is displayed as disabled when the 3 following conditions are met
          //   - A theme from another level is currently selected
          //   - Selected themes don't include one of the current level themes' parent
          //     or, if it does, they include a current theme's parent
          //   - The theme is not currently selected
          const disabled =
            otherThemeLevelSelected &&
            (!hasSelectedParentThemes || !parentThemeSelected) &&
            !themeSelected;

          const labelClassNames = classNames([
            "d-flex",
            "align-items-center",
            "pointer",
            { disabled }
          ]);

          const iconClassNames = classNames([
            "d-inline-block",
            "text-smaller",
            theme.iconClass
          ]);

          return (
            <div key={theme.id} className="theme">
              <label className={labelClassNames}>
                <input
                  type="checkbox"
                  className="flex-shrink-0 margin-right-xs"
                  checked={themeSelected}
                  onChange={() => toggleFilter(theme)}
                  disabled={disabled}
                />

                <span
                  className="icon flex-shrink-0 margin-right-xs text-center"
                  style={{
                    backgroundColor: theme.primaryColor,
                    color: theme.primaryContrastColor
                  }}
                >
                  <Icon className={iconClassNames} />
                </span>
                <span className="text-small">{theme.name}</span>
              </label>
            </div>
          );
        })}
      </fieldset>

      {collapsible && (
        <Button
          className="btn-unstyled pointer text-smaller"
          onClick={() => setShowMore(!showMore)}
          role="button"
          aria-controls={filterContainerDOMId}
          aria-expanded={showMore ? "true" : "false"}
        >
          <b>{showMore ? showLessLabel : showMoreLabel}</b>&nbsp;
          <span className="text-secondary">({themes.length})</span>
        </Button>
      )}
    </div>
  );
};

const ContributionsDatavizSearchFiltersThemes_themes = graphql__b3c9099aae43cfdf6b5d;

export default ContributionsDatavizSearchFiltersThemes;

const intlMessages = defineMessages({
  showMore: {
    id: "participativeModule.contributionsDataviz.filters.showMore",
    defaultMessage: "Afficher plus"
  },
  showLess: {
    id: "participativeModule.contributionsDataviz.filters.showLess",
    defaultMessage: "Afficher moins"
  }
});
